import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { metaContext, track } from "@/lib/meta";
import { oppref, trackConversionPixel } from "@/lib/oaiq";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: on the Google callback route the code is still being exchanged, so skip /me.
    if (window.location.pathname === "/auth/google") {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("nv_token", data.access_token);
    setUser(data.user);
    track("Login", { method: "password" }, { email, externalId: data.user?.id, browserOnly: true });
    return data;
  };

  const subscription = async () => {
    try {
      const { data } = await api.get("/billing/subscription");
      return data;
    } catch {
      return null;
    }
  };

  const register = async (payload) => {
    // Same event_id in the browser and on the server so Meta counts one registration.
    const eventId = `reg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const { data } = await api.post("/auth/register", { ...payload, meta: metaContext(eventId), oppref: oppref() });
    // Dual-fire: the pixel copy and the CAPI copy share eventId, and the server outbox's
    // unique (event_name, event_id) index means Meta is called exactly once.
    track("CompleteRegistration", { status: "password", currency: "INR", value: 0 },
          { eventId, email: payload.email });
    // ChatGPT Ads trial_started: pixel copy of the CAPI event the server just sent.
    trackConversionPixel("trial_started", { type: "plan_enrollment" }, data.oai_event_id);
    // Strict email verification: no session is issued until the address is confirmed.
    if (data.verification_required) return data;
    localStorage.setItem("nv_token", data.access_token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Server-side session teardown failed; still clear the client so the user is signed out.
      console.debug("logout request failed, clearing session locally", e);
    }
    localStorage.removeItem("nv_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser, subscription }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
